import { Injectable } from '@angular/core';
import { ApiService } from '../api/api.service';
import { HttpClient } from "@angular/common/http";
import { Observable, BehaviorSubject } from "rxjs";
import { map, tap } from "rxjs/operators";
import { Opportunity } from '../models/models.lib';
import { APP_CONFIG } from '../setup/config';
import { FormModes } from '../common/constants.lib';
import { environment } from '../../environments/environment';

const { apiUrl } = environment;

export abstract class EnvironerOpportunityService extends BehaviorSubject<Array<any>>{
  public loading: boolean = false;

  constructor(
    private http: HttpClient,
  ) {
    super([]);
  }

  public query(): void {
    this.fetch().subscribe((x) => {
      // console.log('query: x: ', x);
      return super.next(x);
    });
  }

  protected fetch(): Observable<Opportunity[]> {
    this.loading = true;

    return this.http.get(encodeURI(`${apiUrl}/opportunity/all`))
      .pipe(
        map((response: any, i) => {
          // console.log('fetch:map:response: ', response, i);
          // let returning =<Opportunity[]>response; // response.map(r =>  Opportunity.create((r)));
          // console.log('fetch:map:response: ', returning);
          return <Opportunity[]>response;
        }),
        map(r => r.map(x => Opportunity.create(x))),
        // tap(nr => {
        //   console.log('nr: ', nr);
        // }),
        tap(() => (this.loading = false))
      )
  }
}

@Injectable({
  providedIn: 'root'
})
export class OpportunitiesService extends EnvironerOpportunityService {

  constructor(
    http: HttpClient,
    private apiService: ApiService
  ) {
    super(http);
  }

  get() {
    return this.apiService.getHttpCall('opportunity/all', 'get');
  }

  getById(id: number): Promise<any> {
    return this.apiService.getHttpCallAsPromise(`opportunity/${id}`, 'getById');
  }

  add(p: any): Promise<any> {
    return this.apiService.postHttpCallAsPromise('opportunity', p, 'add');
  }

  update(p: any): Promise<any> {
    return this.apiService.putHttpCallAsPromise(`opportunity/${p.id}`, p, 'update');
  }

  destroy(id: number): Promise<any> {
    return this.apiService.deleteHttpCallAsPromise(`opportunity/${id}`, 'destroy');
  }

  addEdit(p: any, formMode: FormModes): Promise<any> {
    if (formMode === FormModes.NEW) {
      return this.add(p);
    } else {
      return this.update(p);
    }
  }

}

/**
 *
{
  "id": 1,
  "title": "Reducing greenhouse gas emissions from an irrigated cotton project",
  "short_description": "An irrigated cotton project can help to reduce the amount of greenhouse gas entering the atmosphere by reducing synthetic fertiliser use while maintaining or increasing yield, or by increasing yield without a proportional increase in fertiliser use.",
  "full_description": "Eligible projects are those growing cotton crops under irrigation, where the emissions of nitrous oxide can be directly related to the rate of synthetic fertiliser nitrogen used on the crop.<br />The emissions avoided by the project are calculated by subtracting the emissions generated during the project from those generated in a defined period before the project began. The difference in emissions before and after the project is the net abatement, which is used to apply for Australian carbon credit units (ACCUs). The crediting period is seven years.<br />A project must be run on one or more cotton farms in any region of the Australian irrigated cotton industry. At least three previous years' worth of cotton crop data is also required for factors such as:<br /><ul><li>area of cotton and green manure (e.g. legumes) planted</li><li>lint yield</li><li>fertiliser use</li></ul>A project involves implementing management actions to increase synthetic nitrogen fertiliser use efficiency. Fertiliser use efficiency is the lint yield of the cotton crop, divided by the amount of synthetic nitrogen fertiliser applied to the crop.<br />You can choose the management actions that best suit your project, as long as they meet the standards given in the method, and are consistent with the relevant myBMP standard published by the Cotton Research and Development Corporation. Some examples of management actions include:<br /><ul><li>changing the type of nitrogen fertiliser</li><li>changing the rate, timing or placement of nitrogen fertiliser application</li><li>improving drainage, or</li><li>growing different crop varieties to increase yield.</li></ul><p>This is the opportunity description on the <a href=\"https://www.cleanenergyregulator.gov.au/ERF/Choosing-a-project-type/Opportunities-for-the-land-sector/Agricultural-methods/reducing-greenhouse-gas-emissions-from-fertiliser-in-irrigated-cotton\" target=\"_blank\">Clean Energy Regulator website</a>.&nbsp; The <a href=\"https://www.cleanenergyregulator.gov.au/ERF/Choosing-a-project-type/Opportunities-for-the-land-sector/Agricultural-methods/reducing-greenhouse-gas-emissions-from-fertiliser-in-irrigated-cotton#Eligibility%20requirements\" target=\"_blank\">eligibility criteria can be found here</a>.</p>",
  "opportunity_goals": [
    {
      "id": 1,
      "goal": "Environmental Stewardship"
    },
    {
      "id": 5,
      "goal": "Carbon Neutrality"
    },
    {
      "id": 6,
      "goal": "Carbon"
    },
    {
      "id": 7,
      "goal": "Soil Carbon"
    },
    {
      "id": 8,
      "goal": "Carbon Farming"
    },
    {
      "id": 9,
      "goal": "Emission Reduction"
    },
    {
      "id": 10,
      "goal": "Sustainability"
    }
  ],
  "classification_id": 1,
  "classification_description": "Access Market Opportunity (accreditation /certification)",
  "industries": [
    {
      "id": 9,
      "description": "Extensive Cropping"
    }
  ],
  "states": [
    {
      "id": 1,
      "description": "QLD"
    },
    {
      "id": 2,
      "description": "NSW"
    },
    {
      "id": 3,
      "description": "NT"
    },
    {
      "id": 4,
      "description": "WA"
    },
    {
      "id": 5,
      "description": "SA"
    },
    {
      "id": 6,
      "description": "TAS"
    },
    {
      "id": 7,
      "description": "VIC"
    },
    {
      "id": 8,
      "description": "ACT"
    }
  ],
  "organisation_name": "Australian Government Clean Energy Regulator",
  "organisation_url": "https://www.cleanenergyregulator.gov.au/",
  "service_providers": null,
  "benefit_types": [
    {
      "id": 2,
      "description": "Financial"
    }
  ],
  "benefits": "Improvement in nitrogen use efficiency for cotton crops.",
  "negatives": null,
  "risks": "There may be a need to seek professional support for changes in management actions.",
  "costs": "Reporting requirements to the Clean Energy Regulator / Extra tests and monitoring as set out in the cotton myBMP standard.",
  "attribution": "Australian Government Clean Energy Regulator",
  "funding_scheme_start": null,
  "funding_scheme_end": null,
  "potential_financial_benefits": "The spot and auction prices of an ACCU with quarterly carbon market reports are <a href=\"https://www.cleanenergyregulator.gov.au/ERF/auctions-results\" target=\"_blank\">published here.</a>",
  "updated_by_user_id": -9,
  "updated_by_user": "Julie Fookes (Dev-Admin)",
  "updated_on": "2024-02-13",
  "deleted": false,
  "co_benefits": "2",
  "demonstrated_financial_benefit": 2,
  "certification_accreditation_scheme": 1,
  "anz_iso_certification": 0,
  "demonstrated_access_to_markets": 1,
  "public_information": 1,
  "information_quality": 0,
  "total_ranking": 5,
  "practices": "0",
  "reference_links": "0",
  "organisation_contact_email": "",
  "elegibility_json": [],
  "elegibility_text": null,
  "demonstrated_risks": 0,
  "demonstrated_costs": 0,
  "demonstrated_non_financial_benefits": 0
},

 */
